import config from 'config/common';
import store, { RootState } from 'store/index';
import { ThemeOptions } from 'interfaces/main';
import i18n from 'i18n';
import mixPanel, { mixPanelEventsTypes } from 'helpers/mixPanel';

type ProLineCallbacks = {
    onShow?: (stats: any) => void;
    onCopyToClipboard?: (uniqueLink: string, stats: any) => void;
    onSocialClicked?: (platform: string, stats: any) => void;
    onDateChange?: (selection: any) => void;
    onTooltip?: (kpiLabel: string, stats: any) => void;
    onTermsClicked?: (termsUrl: string, stats: any) => void;
};

function createProLineCallbacks(callbacks?: ProLineCallbacks) {
    const trackEvent = (eventType: any, payload: any) => {
        try {
            mixPanel.track(eventType, payload);
        } catch (error) {
            // Silently handle analytics errors
        }
    };

    let isInitialDateChange = true;

    return {
        onShow: (stats: any) => {
            // If stats is an array, use the first element. Otherwise, use stats as-is.
            const normalizedStats = Array.isArray(stats) ? stats[0] : stats;

            trackEvent(mixPanelEventsTypes.RAF_POPUP_VIEWED, {
                KPI: normalizedStats,
            });
            if (callbacks?.onShow) {
                callbacks.onShow(stats);
            }
        },
        onCopyToClipboard: (uniqueLink: string, stats: any) => {
            const normalizedStats = Array.isArray(stats) ? stats[0] : stats;
            trackEvent(mixPanelEventsTypes.RAF_LINK_COPIED, {
                referral_link_id: uniqueLink,
                KPI: normalizedStats,
            });
            if (callbacks?.onCopyToClipboard) {
                callbacks.onCopyToClipboard(uniqueLink, stats);
            }
        },
        onSocialClicked: (platform: string, stats: any) => {
            trackEvent(mixPanelEventsTypes.RAF_SOCIAL_SHARE_CLICKED, {
                platform,
            });
            if (callbacks?.onSocialClicked) {
                callbacks.onSocialClicked(platform, stats);
            }
        },
        onDateChange: (selection: any) => {
            if (isInitialDateChange) {
                isInitialDateChange = false;
                if (callbacks?.onDateChange) {
                    callbacks.onDateChange(selection);
                }
                return;
            }
            trackEvent(mixPanelEventsTypes.RAF_TIME_FILTER_CHANGED, {
                selected_time_filter: selection,
            });
            if (callbacks?.onDateChange) {
                callbacks.onDateChange(selection);
            }
        },
        onTooltip: (kpiLabel: string, stats: any) => {
            trackEvent(mixPanelEventsTypes.RAF_TOOLTIP_OPENED, {
                tooltip_target: kpiLabel,
            });
            if (callbacks?.onTooltip) {
                callbacks.onTooltip(kpiLabel, stats);
            }
        },
        onTermsClicked: (termsUrl: string, stats: any) => {
            trackEvent(mixPanelEventsTypes.RAF_TERMS_LINK_CLICKED, {});
            if (callbacks?.onTermsClicked) {
                callbacks.onTermsClicked(termsUrl, stats);
            }
        },
    };
}

export async function showReferAndEarnModal(callbacks?: ProLineCallbacks): Promise<void> {
    const state = store.getState() as unknown as RootState;
    const user = state.user?.userInfo;
    const language = i18n?.language || 'en';
    const theme = state.system?.theme ?? ThemeOptions.LIGHT;

    if (!config.proLine.enabled) {
        throw new Error('ProLine feature disabled');
    }

    const referInstance = (window as any).referInstance;
    if (!referInstance || typeof referInstance.show !== 'function') {
        throw new Error('ProLine script not loaded');
    }

    const encryptedCustomerId = user?.prolineEncryptedCustomerId;
    const customerId = user?.id;

    if (!encryptedCustomerId) {
        throw new Error('Missing encrypted customer id');
    }
    const proLineCallbacks = createProLineCallbacks(callbacks);

    referInstance.show({
        theme,
        language,
        customerId,
        encryptedCustomerId,
        callbacks: proLineCallbacks,
    });
}
